import { apiRequest, resolveApiBaseUrl } from '../utils/apiClient';

export type InvoiceStatus = 'ISSUED' | 'PAID' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'CANCELLED';
export type InvoiceKind = 'SIMPLIFIED' | 'STANDARD';
export type BuyerType = 'INDIVIDUAL' | 'COMPANY';

export interface InvoiceItemLite {
  description: string;
  descriptionAr?: string | null;
}

export interface InvoiceSummary {
  id: string;
  invoiceNumber: string;
  invoiceKind: InvoiceKind;
  status: InvoiceStatus;
  issuedAt: string;
  totalSar: number;
  vatAmountSar: number;
  currency: string;
  paymentMethod: string | null;
  zatcaStatus: string;
  lineCount: number;
  items: InvoiceItemLite[];
}

export interface InvoicesPage {
  data: InvoiceSummary[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface BillingProfile {
  legalName: string | null;
  buyerType: BuyerType;
  vatNumber: string | null;
  crNumber: string | null;
  address: string | null;
  billingEmail: string | null;
}

export interface SubscriptionSummary {
  id: string;
  planId: string;
  planName: string | null;
  status: 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';
  billingCycle: string;
  currentPeriodEnd: string;
  nextBillingAt: string;
  gracePeriodEnd: string | null;
  autoRenew: boolean;
  autoRenewLive: boolean;
}

async function fetchAuthed(path: string, token?: string | null): Promise<Response> {
  const base = resolveApiBaseUrl();
  return fetch(`${base}${path.startsWith('/') ? path : `/${path}`}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const invoiceService = {
  async getInvoices(token: string, opts: { page?: number; pageSize?: number } = {}): Promise<InvoicesPage> {
    const qs = new URLSearchParams({ page: String(opts.page ?? 1), pageSize: String(opts.pageSize ?? 20) });
    return apiRequest<InvoicesPage>(`/invoices?${qs.toString()}`, { token });
  },

  async getInvoice(token: string, id: string): Promise<{ data: unknown }> {
    return apiRequest<{ data: unknown }>(`/invoices/${id}`, { token });
  },

  async getBillingProfile(token: string): Promise<{ data: BillingProfile }> {
    return apiRequest<{ data: BillingProfile }>(`/billing-profile`, { token });
  },

  async updateBillingProfile(token: string, body: Partial<BillingProfile>): Promise<{ data: BillingProfile }> {
    return apiRequest<{ data: BillingProfile }>(`/billing-profile`, { method: 'PUT', token, body });
  },

  async getSubscription(token: string): Promise<{ data: SubscriptionSummary | null }> {
    return apiRequest<{ data: SubscriptionSummary | null }>(`/billing/subscription`, { token });
  },

  async setAutoRenew(token: string, autoRenew: boolean): Promise<{ data: SubscriptionSummary | null }> {
    return apiRequest<{ data: SubscriptionSummary | null }>(`/billing/subscription/auto-renew`, {
      method: 'PUT',
      token,
      body: { autoRenew },
    });
  },

  /** Open the bilingual invoice document in a new tab (auth via fetch → blob URL). */
  async openInvoiceHtml(token: string, id: string): Promise<void> {
    const res = await fetchAuthed(`/invoices/${id}/html`, token);
    if (!res.ok) throw new Error('Failed to load invoice');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /**
   * Download the PDF. If the server has no PDF generator yet (409), transparently
   * fall back to the printable HTML invoice. Returns which artifact was delivered.
   */
  async downloadInvoicePdf(token: string, id: string, invoiceNumber: string): Promise<'pdf' | 'html'> {
    const res = await fetchAuthed(`/invoices/${id}/pdf`, token);
    const contentType = res.headers.get('content-type') || '';
    if (res.ok && contentType.includes('application/pdf')) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return 'pdf';
    }
    // PDF unavailable on this host → open the printable HTML view instead.
    await this.openInvoiceHtml(token, id);
    return 'html';
  },
};

export default invoiceService;
